// frontend/src/components/Products/ProductForm.jsx
import React, { useState } from 'react';
import { productService } from '../../services/api';
import { toast } from 'react-hot-toast';
import { ShoppingBagIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

export default function ProductForm({ product, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    sku: product?.sku || '',
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || '',
    product_type: product?.product_type || 'product',
    unit_type: product?.unit_type || 'piece',
    unit_price: product?.unit_price || '',
    cost_price: product?.cost_price || '',
    tax_rate: product?.tax_rate || 16,
    current_stock: product?.current_stock || '',
    min_stock_level: product?.min_stock_level || '',
    barcode: product?.barcode || '',
    is_taxable: product?.is_taxable !== undefined ? product?.is_taxable : true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.sku || formData.sku.trim() === '') {
      toast.error('SKU is required');
      return;
    }
    
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Name is required');
      return;
    }
    
    if (!formData.unit_price || formData.unit_price <= 0) {
      toast.error('Valid price is required');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        sku: formData.sku?.trim() || null,
        name: formData.name?.trim() || null,
        description: formData.description?.trim() || null,
        category: formData.category?.trim() || null,
        product_type: formData.product_type,
        unit_type: formData.unit_type,
        unit_price: parseFloat(formData.unit_price) || 0,
        cost_price: formData.product_type === 'service' ? 0 : (parseFloat(formData.cost_price) || 0),
        tax_rate: formData.is_taxable ? (parseFloat(formData.tax_rate) || 16) : 0,
        current_stock: formData.product_type === 'service' ? 0 : (parseInt(formData.current_stock) || 0),
        min_stock_level: formData.product_type === 'service' ? 0 : (parseInt(formData.min_stock_level) || 0),
        barcode: formData.product_type === 'service' ? null : (formData.barcode || null),
        is_taxable: formData.is_taxable
      };
      
      console.log('Submitting:', submitData);
      
      if (product) {
        await productService.updateProduct(product.id, submitData);
        toast.success(submitData.product_type === 'service' ? 'Service updated successfully' : 'Product updated successfully');
      } else {
        await productService.createProduct(submitData);
        toast.success(submitData.product_type === 'service' ? 'Service created successfully' : 'Product created successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Form error:', error);
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Product Type Selection */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Item Type <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-4">
          <label className={`flex items-center space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all flex-1 ${
            formData.product_type === 'product' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              value="product"
              checked={formData.product_type === 'product'}
              onChange={(e) => setFormData({ 
                ...formData, 
                product_type: e.target.value, 
                current_stock: '', 
                cost_price: '',
                barcode: '',
                unit_type: 'piece'
              })}
              className="h-4 w-4 text-blue-600"
            />
            <ShoppingBagIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium">Physical Product</span>
            <span className="text-xs text-gray-500">(Tracks inventory)</span>
          </label>
          
          <label className={`flex items-center space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all flex-1 ${
            formData.product_type === 'service' 
              ? 'border-purple-500 bg-purple-50' 
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              value="service"
              checked={formData.product_type === 'service'}
              onChange={(e) => setFormData({ 
                ...formData, 
                product_type: e.target.value, 
                current_stock: '0', 
                cost_price: '0',
                barcode: '',
                unit_type: 'hour'
              })}
              className="h-4 w-4 text-purple-600"
            />
            <WrenchScrewdriverIcon className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium">Service</span>
            <span className="text-xs text-gray-500">(Labour, Installation, Washing)</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* SKU */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SKU / Service Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            placeholder={formData.product_type === 'service' ? 'e.g., INSTALL-001' : 'e.g., PROD-001'}
          />
        </div>

        {/* Name */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formData.product_type === 'service' ? 'Service Name' : 'Product Name'} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            placeholder={formData.product_type === 'service' ? 'e.g., TV Installation' : 'e.g., Samsung TV'}
          />
        </div>

        {/* Category */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <input
            type="text"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={formData.product_type === 'service' ? 'e.g., Installation' : 'e.g., Electronics'}
          />
        </div>

        {/* Unit Type (for services) */}
        {formData.product_type === 'service' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Billing Unit
            </label>
            <select
              value={formData.unit_type}
              onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="piece">Per Job (Fixed Price)</option>
              <option value="hour">Per Hour</option>
              <option value="day">Per Day</option>
              <option value="month">Per Month</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How customers will be charged for this service
            </p>
          </div>
        )}

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formData.product_type === 'service' ? 'Rate' : 'Price'} (Excl. VAT) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.unit_price}
            onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            placeholder="0.00"
          />
          {formData.product_type === 'service' && (
            <p className="text-xs text-gray-500 mt-1">Price per {formData.unit_type} (VAT will be added)</p>
          )}
          {formData.product_type === 'product' && (
            <p className="text-xs text-gray-500 mt-1">Customer will pay +16% VAT on checkout</p>
          )}
        </div>

        {/* Cost Price (only for products) */}
        {formData.product_type === 'product' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost Price (KES)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.cost_price}
              onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Your purchase cost (for profit calculation)</p>
          </div>
        )}

        {/* Tax Settings */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tax Rate (%)
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="number"
              step="0.5"
              value={formData.tax_rate}
              onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!formData.is_taxable}
            />
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_taxable}
                onChange={(e) => setFormData({ ...formData, is_taxable: e.target.checked, tax_rate: e.target.checked ? 16 : 0 })}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm">Taxable (VAT applies)</span>
            </label>
          </div>
        </div>

        {/* Stock Fields (only for products) */}
        {formData.product_type === 'product' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Initial Stock
              </label>
              <input
                type="number"
                value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Stock Level
              </label>
              <input
                type="number"
                value={formData.min_stock_level}
                onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this level</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Barcode
              </label>
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
          </>
        )}

        {/* Service-specific info */}
        {formData.product_type === 'service' && (
          <div className="col-span-2">
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>💡 Service Info:</strong> This service will not track inventory. 
                You can sell it by {formData.unit_type === 'piece' ? 'job' : formData.unit_type} and it will appear on invoices.
              </p>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder={formData.product_type === 'service' ? 'Describe the service...' : 'Product description...'}
          />
        </div>
      </div>

      <div className="flex space-x-3 pt-4">
        <button 
          type="submit" 
          disabled={loading} 
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            product ? 'Update' : (formData.product_type === 'service' ? 'Create Service' : 'Create Product')
          )}
        </button>
        <button 
          type="button" 
          onClick={onCancel} 
          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}